import { decodeJwtToken } from './decodeJwtToken';
import { JWT_EXPIRY_BUFFER_SECONDS, JWT_REFRESH_THRESHOLD_SECONDS } from './constants';

/**
 * Parsed JWT token with commonly-used fields extracted
 */
export type ParsedJwtToken = {
  uuid: string | null;
  email: string | null;
  role: string | null;
  isStale: boolean;
  isExpired: boolean;
  /** Expired, or less than JWT_EXPIRY_BUFFER_SECONDS left */
  isExpiringSoon: boolean;
};

/**
 * Parse JWT token and extract commonly-used fields
 *
 * @param token - JWT token string
 * @returns Parsed token object or null if invalid
 *
 * @example
 * const parsed = parseJwtToken(token);
 * if (!parsed || parsed.isStale) {
 *   // Refresh needed
 * }
 *
 * console.log(parsed.uuid, parsed.email, parsed.role);
 */
export function parseJwtToken(token: string | undefined): ParsedJwtToken | null {
  const payload = decodeJwtToken(token);

  if (!payload) {
    return null;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  const issuedAt = payload.iat;
  const expiresAt = payload.exp;

  const isTooOld = !issuedAt || nowInSeconds - issuedAt > JWT_REFRESH_THRESHOLD_SECONDS;
  const isExpired = !expiresAt || nowInSeconds >= expiresAt;
  const isExpiringSoon = !expiresAt || nowInSeconds >= expiresAt - JWT_EXPIRY_BUFFER_SECONDS;
  const isStale = isTooOld || isExpired;

  return {
    uuid: payload.uuid ?? null,
    email: payload.email ?? null,
    role: payload.auth ?? null,
    isStale,
    isExpired,
    isExpiringSoon,
  };
}
